import { useCallback, useEffect, useMemo } from 'react';
import { useBlocker } from 'react-router-dom';
import { UploadState, UploadStatus, useUpload } from 'src/hooks/use_upload';

/**
 * Prevents accidental page navigation while file uploads are in progress.
 * Shows a browser confirmation on refresh/close and exposes state for an in-app dialog.
 */
export const usePreventLeaveDuringUpload = (
  orderId: string,
  enabled = true,
) => {
  const { getUploadsForOrder } = useUpload();

  const hasPendingUploads = useMemo(
    () => enabled && hasPendingUploadFilesForOrder(getUploadsForOrder, orderId),
    [enabled, getUploadsForOrder, orderId],
  );

  const shouldBlockNavigation = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string; search: string };
      nextLocation: { pathname: string; search: string };
    }) => {
      if (!hasPendingUploads) {
        return false;
      }

      return (
        currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search
      );
    },
    [hasPendingUploads],
  );

  const blocker = useBlocker(shouldBlockNavigation);

  useEffect(() => {
    if (!hasPendingUploads) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingUploads]);

  const isNavigationBlocked = blocker.state === 'blocked';

  const confirmLeave = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const cancelLeave = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  return {
    hasPendingUploads,
    isNavigationBlocked,
    confirmLeave,
    cancelLeave,
  };
};

const hasPendingUploadFilesForOrder = (
  getUploadsForOrder: (orderId: string) => UploadState[],
  orderId: string,
): boolean =>
  getUploadsForOrder(orderId).some((upload) =>
    [UploadStatus.Queued, UploadStatus.InProgress].includes(upload.status),
  );
